/**
 * FixCardPanel — Human-in-the-Loop AI Fix Assistant
 *
 * Displays one card per compile error. Each card shows:
 *   - Plain-English explanation (non-technical)
 *   - Root cause
 *   - Proposed fix: line X — change "old" to "new"
 *   - [Apply Fix] [Fix It Myself] buttons
 *   - COBOL source comment (if present — the business rule context)
 *
 * No code editor required to apply a mechanical fix.
 * "Fix It Myself" opens the editor at the exact error line.
 */

import { useState } from 'react'
import {
  Wand2, CheckCircle2, AlertTriangle, Loader2, ArrowRight,
  Code2, FileText, ChevronDown, ChevronUp, Wrench, X
} from 'lucide-react'
import './FixCardPanel.css'

// ── Confidence badge ──────────────────────────────────────────────────────────

function ConfidenceBadge({ confidence }) {
  const cls = confidence === 'high' ? 'high' : confidence === 'medium' ? 'medium' : 'low'
  const label = confidence === 'high' ? 'High Confidence' : confidence === 'medium' ? 'Medium' : 'Manual Review'
  return <span className={`fix-confidence ${cls}`}>{label}</span>
}

// ── Category label ────────────────────────────────────────────────────────────

function CategoryLabel({ category }) {
  const labels = {
    syntax:    'Syntax Error',
    symbol:    'Missing Symbol',
    type:      'Type Mismatch',
    structure: 'Structure Error',
    null:      'Null Reference',
    unknown:   'Compile Error',
  }
  return <span className="fix-category">{labels[category] || 'Compile Error'}</span>
}

// ── Single fix card ───────────────────────────────────────────────────────────

function FixCard({ card, isApplied, isRolledBack, isApplying, onApply, onFixMyself }) {
  const [showDetails, setShowDetails] = useState(false)
  const cardKey = `${card.file}:${card.line}`

  const canApply    = card.can_auto_fix && !isApplied && !isRolledBack
  const showApplied = isApplied
  const showFailed  = isRolledBack

  return (
    <div className={`fix-card ${isApplied ? 'applied' : ''} ${isRolledBack ? 'rolled-back' : ''}`}>

      {/* Header row */}
      <div className="fix-card-header">
        <div className="fix-card-location">
          <Code2 size={13} />
          <span className="fix-file">{card.file}</span>
          <span className="fix-line">Line {card.line}</span>
        </div>
        <div className="fix-card-badges">
          <CategoryLabel category={card.category} />
          <ConfidenceBadge confidence={card.confidence} />
        </div>
      </div>

      {/* Applied / rolled-back overlay */}
      {showApplied && (
        <div className="fix-card-applied-banner">
          <CheckCircle2 size={14} /> Fix applied — pre-flight re-ran
        </div>
      )}
      {showFailed && (
        <div className="fix-card-rollback-banner">
          <AlertTriangle size={14} /> Fix rolled back — made things worse. Try "Fix It Myself".
        </div>
      )}

      {/* Plain-English explanation */}
      <div className="fix-explanation">
        <span className="fix-label">What's wrong</span>
        <p>{card.explanation || 'No explanation available.'}</p>
      </div>

      {/* Root cause */}
      {card.root_cause && (
        <div className="fix-root-cause">
          <span className="fix-label">Why it happened</span>
          <p>{card.root_cause}</p>
        </div>
      )}

      {/* COBOL business rule context */}
      {card.cobol_comment && (
        <div className="fix-cobol-context">
          <FileText size={12} />
          <span className="fix-cobol-label">Original COBOL rule:</span>
          <code>{card.cobol_comment}</code>
        </div>
      )}

      {/* Proposed fix */}
      {card.can_auto_fix && card.fix && (
        <div className="fix-proposal">
          <span className="fix-label">Proposed fix</span>
          <div className="fix-diff">
            <div className="fix-diff-row remove">
              <span className="diff-sign">−</span>
              <code>{card.fix.old_text}</code>
            </div>
            <div className="fix-diff-row add">
              <span className="diff-sign">+</span>
              <code>{card.fix.new_text}</code>
            </div>
            <div className="fix-diff-desc">{card.fix.description}</div>
          </div>
        </div>
      )}

      {!card.can_auto_fix && !isApplied && (
        <div className="fix-manual-note">
          <AlertTriangle size={13} />
          This error requires manual review — the fix cannot be applied automatically.
        </div>
      )}

      {/* Action buttons */}
      {!isApplied && !isRolledBack && (
        <div className="fix-card-actions">
          {canApply && (
            <button
              className="fix-btn-apply"
              onClick={() => onApply(card)}
              disabled={isApplying}
            >
              {isApplying
                ? <><Loader2 size={13} className="spinning" /> Applying...</>
                : <><CheckCircle2 size={13} /> Apply Fix</>
              }
            </button>
          )}
          <button
            className="fix-btn-manual"
            onClick={() => onFixMyself(card)}
            disabled={isApplying}
          >
            <Wrench size={13} /> Fix It Myself
          </button>
        </div>
      )}

      {/* Collapsible details (error message + line range for devs) */}
      <button
        className="fix-details-toggle"
        onClick={() => setShowDetails(v => !v)}
      >
        {showDetails ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        {showDetails ? 'Hide details' : 'Technical details'}
      </button>

      {showDetails && (
        <div className="fix-details">
          <div><strong>Compiler message:</strong> {card.error_message}</div>
          <div><strong>Context lines:</strong> {card.line_range?.[0]}–{card.line_range?.[1]}</div>
          {card.ai_error && <div className="fix-ai-error"><strong>AI error:</strong> {card.ai_error}</div>}
        </div>
      )}
    </div>
  )
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function FixCardPanel({
  analyzeState,
  onApplyFix,
  onFixMyself,
  onDismiss,
  STATE_LOADING,
  STATE_READY,
  STATE_APPLYING,
  STATE_APPLIED,
  STATE_ERROR,
}) {
  const { phase, cards, message, error, applying, applied, rolled_back } = analyzeState

  if (phase === STATE_LOADING) {
    return (
      <div className="fix-card-panel loading">
        <Loader2 size={20} className="spinning" />
        <p>Analyzing errors with Claude…</p>
        <p className="fix-loading-sub">Reading COBOL context, classifying errors, proposing fixes.</p>
      </div>
    )
  }

  if (phase === STATE_ERROR && !cards?.length) {
    return (
      <div className="fix-card-panel error">
        <AlertTriangle size={20} />
        <p>Could not analyze errors</p>
        <p className="fix-error-detail">{error}</p>
        <button className="fix-btn-dismiss" onClick={onDismiss}>
          <X size={13} /> Dismiss
        </button>
      </div>
    )
  }

  if (phase !== STATE_READY && phase !== STATE_APPLYING && phase !== STATE_APPLIED && phase !== STATE_ERROR) {
    return null
  }

  const autoFixable = cards.filter(c => c.can_auto_fix).length

  return (
    <div className="fix-card-panel">

      {/* Panel header */}
      <div className="fix-panel-header">
        <div className="fix-panel-title">
          <Wand2 size={16} />
          <span>AI Fix Assistant</span>
          <span className="fix-panel-count">{cards.length} issue{cards.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="fix-panel-close" onClick={onDismiss}>
          <X size={14} />
        </button>
      </div>

      {/* Summary */}
      <div className="fix-panel-summary">
        {autoFixable > 0
          ? <><CheckCircle2 size={13} /> {autoFixable} of {cards.length} can be auto-fixed. Review each card below.</>
          : <><AlertTriangle size={13} /> These errors require manual review. Use "Fix It Myself" to open the editor.</>
        }
      </div>

      {/* Error banner (regression rollback etc) */}
      {error && (
        <div className="fix-panel-error-banner">
          <AlertTriangle size={13} /> {error}
        </div>
      )}

      {/* Fix cards */}
      <div className="fix-cards-list">
        {cards.map((card) => {
          const key     = `${card.file}:${card.line}:${card.error_message || ''}`
          const isApplied    = (applied || []).includes(key)
          const isRolledBack = (rolled_back || []).includes(key)
          const isApplying   = applying === key
          return (
            <FixCard
              key={key}
              card={card}
              isApplied={isApplied}
              isRolledBack={isRolledBack}
              isApplying={isApplying}
              onApply={onApplyFix}
              onFixMyself={onFixMyself}
            />
          )
        })}
      </div>

      {/* Message */}
      {message && (
        <div className="fix-panel-message">{message}</div>
      )}
    </div>
  )
}
